// react
import { ReactNode, useMemo } from 'react';

// types
import { Form, Pagination } from '../../../../@types';

// i18next
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { Grid } from '@mui/material';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardForm } from '../../../cards/CardForm/CardForm';

// ui
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// icons
import { ViewListOutlined as IconList } from '@mui/icons-material';

// props
export interface TableFormsProps {
	forms: Form[];
	isLoading?: boolean;
	pagination: Pagination;
}

export const TableForms = ({ forms, isLoading, pagination }: TableFormsProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [searchParams] = useSearchParams();

	// params
	const paramQ = searchParams.get('q');

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 4,
			},
			isSortable: true,
			label: t('form'),
			value: 'name',
		},
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('updated'),
			value: 'modified',
		},
		{
			GridProps: {
				lg: 4,
			},
			label: t('user'),
			value: 'user',
		},
	];

	// empty
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			content: { children: t('empty.forms.content') },
			IconCircleProps: { icon: (<IconList />) as ReactNode },
			title: { children: t('empty.forms.title') },
		};
		if (paramQ) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, t]);

	// content
	const content = useMemo(() => {
		if (isLoading || !forms.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{forms.map((form, i) => {
					return (
						<Grid item key={`form-${i}`} xs={12}>
							<CardForm form={form} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, isLoading, forms]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={forms.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
